// src/app/service/collection.service.ts

import { Injectable } from '@angular/core'
import { Apollo } from 'apollo-angular'
import { Observable } from 'rxjs'
import { tap, map } from 'rxjs/operators'
import {
  CollectionQuery,
  CollectionDocument,
  ProductVariant,
  ProductVariantList,
} from '../gql/generated'
import {
  homepageCarouselActions,
  preOrdersActions,
  bestSellerActions,
  flashDiscountActions,
} from '../store/collection-stores'
import { CollectionSlug } from '../enums/collection.enums'
import { LoaderService } from './loader.service'

@Injectable({
  providedIn: 'root',
})
export class CollectionService {
  constructor(
    private apollo: Apollo,
    private loaderService: LoaderService
  ) {}

  fetchCollection(
    slug: CollectionSlug | string,
    tab?: string
  ): Observable<CollectionQuery['collection'] | null> {
    this.setCollectionState(slug, null)

    const slugWithTab = tab ? `${slug}-${tab}` : slug

    return this.loaderService.withLoader(
      () =>
        this.apollo
          .query<CollectionQuery>({
            query: CollectionDocument,
            variables: { slug: slugWithTab },
            fetchPolicy: 'network-only',
          })
          .pipe(
            map((result) => {
              const collection = result.data
                .collection as CollectionQuery['collection']
              if (collection) {
                return {
                  ...collection,
                  productVariants: {
                    ...(collection.productVariants as ProductVariantList),
                    items:
                      collection.productVariants.items.length > 0
                        ? (collection.productVariants.items as ProductVariant[])
                        : [],
                  },
                }
              }
              return null
            }),
            tap({
              next: (collection) => {
                this.setCollectionState(slug, collection)
              },
              error: (error) => {
                console.error('Error fetching collection:', error)
                this.setErrorState(slug, 'Failed to fetch collection')
              },
            })
          ),
      slug
    )
  }

  private setCollectionState(
    slug: CollectionSlug | string,
    collection: CollectionQuery['collection'] | null
  ) {
    switch (slug) {
      case CollectionSlug.HOMEPAGE_CAROUSEL:
        homepageCarouselActions.setCollection(collection)
        break
      case CollectionSlug.PRE_ORDERS:
        preOrdersActions.setCollection(collection)
        break
      case CollectionSlug.BEST_SELLER:
        bestSellerActions.setCollection(collection)
        break
      case CollectionSlug.FLASH_DISCOUNT:
        flashDiscountActions.setCollection(collection)
        break
      default:
        break
    }
  }

  getLoaderState(slug: string): Observable<boolean> {
    return this.loaderService.getLoaderState(slug)
  }

  private setErrorState(slug: CollectionSlug | string, error: string | null) {
    switch (slug) {
      case CollectionSlug.HOMEPAGE_CAROUSEL:
        homepageCarouselActions.setError(error)
        break
      case CollectionSlug.PRE_ORDERS:
        preOrdersActions.setError(error)
        break
      case CollectionSlug.BEST_SELLER:
        bestSellerActions.setError(error)
        break
      case CollectionSlug.FLASH_DISCOUNT:
        flashDiscountActions.setError(error)
        break
      default:
        break
    }
  }
}
